import moment from 'moment';
import { useCallback, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import classNames from 'classnames';
// API
import { useAuth } from '../../../../../hooks/useAuth';
import { useEnv } from '../../../../../context/env-context';
import { makeRequest } from '../../../../../utils/makeRequest';
import { toast } from 'react-toastify';
// constants
import { CARD_STATUS } from '../../../../../constants/consumers';
// icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
// components
import CardProviderImage from '../../../../../components/CardProviderImage';
import ConsumerSelectInput from '../../shared/ConsumerSelectInput';
// forms
import ConsumerPopoverNewCardForm from '../forms/ConsumerPopoverNewCardForm';

function ConsumerTabaPayCardsSection({
  tabaPayCards,
  getCards,
  getAstraUser,
  sendCardRequest,
  getBankAccountValue,
  cardsBlockStatus,
  getCardsBlockStatus,
  unblockLinkStatus,
  consumerId,
  consumerFirstName,
  consumerLastName,
  consumerAddressLine1,
  consumerAddressLine2,
  consumerCity,
  consumerState,
  consumerZip,
  consumerManualCardValidation,
  consumerAllowAdHocCardException,
  setConsumer,
}) {
  const { apiOriginConsumer } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [showLog, setShowLog] = useState([]);
  const [moreInfo, setMoreInfo] = useState([]);

  const updateConsumer = useCallback(
    async fields => {
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = {
        token,
        url: `${apiOriginConsumer}/update_consumer`,
        method: 'POST',
        data: JSON.stringify({ consumerId, ...fields }),
      };

      try {
        const response = await makeRequest(config);

        if (response.status === 'ok') {
          setConsumer(prevConsumer => ({ ...prevConsumer, ...fields }));
        }
      } catch (error) {
        toast.error(error.message);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAccessTokenSilently, apiOriginConsumer],
  );

  return (
    <div>
      <div className="d-flex align-items-center gap-1">
        <ConsumerPopoverNewCardForm
          buttonTitle="Link New Card"
          getAstraUser={getAstraUser}
          getCards={getCards}
          cardsBlockStatus={cardsBlockStatus}
          getCardsBlockStatus={getCardsBlockStatus}
          consumerId={consumerId}
          consumerFirstName={consumerFirstName}
          consumerLastName={consumerLastName}
          consumerAddressLine1={consumerAddressLine1}
          consumerAddressLine2={consumerAddressLine2}
          consumerCity={consumerCity}
          consumerState={consumerState}
          consumerZip={consumerZip}
        />
      </div>
      {cardsBlockStatus.blocked && (
        <div className="d-flex align-items-end gap-2 mt-2">
          <p className="bo-consumer-text-sm text-danger text-nowrap text-truncate">
            Blocked for linking a new card until{' '}
            {moment.utc(cardsBlockStatus.blockedUntil).local().format('h:mm a')}
          </p>
          <Button className="bo-common-inline-button text-nowrap" onClick={unblockLinkStatus}>
            <p className="bo-consumer-text-bold">Unblock</p>
          </Button>
        </div>
      )}
      <div>
        <ConsumerSelectInput
          label="Manual Card Validation"
          initialValue={consumerManualCardValidation ? 'Yes' : 'No'}
          labelClassName="mt-3"
          options={[
            { key: 'Yes', value: 'Yes', name: 'Yes' },
            { key: 'No', value: 'No', name: 'No' },
          ]}
          onSave={value => updateConsumer({ manualCardValidation: value === 'Yes' })}
        />
        <ConsumerSelectInput
          label="Allow AdHoc Card Exception"
          initialValue={consumerAllowAdHocCardException ? 'Yes' : 'No'}
          labelClassName="mt-3"
          options={[
            { key: 'Yes', value: 'Yes', name: 'Yes' },
            { key: 'No', value: 'No', name: 'No' },
          ]}
          onSave={value => updateConsumer({ allowAdHocCardException: value === 'Yes' })}
        />
      </div>
      <div className="mt-3">
        {tabaPayCards.map(card => (
          <Card
            className={classNames(
              'mb-3 p-3 bo-max-w-400 overflow-hidden',
              card.isAdhoc && 'bo-card-adhoc-bg-color',
            )}
            key={card.cardId}
          >
            <div className="d-flex justify-content-between align-items-center">
              <CardProviderImage height={12} company={card.company} />
              <div className="d-flex align-items-center">
                <p className="bo-consumer-text me-1">{card.type?.toUpperCase()}</p>
                {card.isMain && <FontAwesomeIcon icon={faStar} size="sm" color="orange" />}
              </div>
            </div>
            <div className="mt-3">
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-consumer-text bo-w-150">Card Number:</p>
                <p className="bo-consumer-text text-nowrap">
                  {`**** ***** **** ${card.last4Digits}`}
                </p>
              </div>
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-consumer-text bo-w-150">EXP:</p>
                <p className={`bo-consumer-text ${card.isExpired ? 'bo-text-red' : ''}`}>
                  {`${card.expiration} ${card.isExpired ? 'Expired!' : ''}`}
                </p>
              </div>
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-consumer-text bo-min-w-150">Card Holder:</p>
                <p className="bo-consumer-text text-truncate">
                  {`${card.firstName} ${card.lastName}`}
                </p>
              </div>
              <hr className="my-2" />
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-consumer-text bo-w-150">Push enabled:</p>
                <p className="bo-consumer-text">
                  {card.pushEnabled ? card.pushAvailability : 'No'}
                </p>
              </div>
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-consumer-text bo-w-150">Pull enabled:</p>
                <p className="bo-consumer-text">{card.pullEnabled ? 'Yes' : 'No'}</p>
              </div>
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-consumer-text bo-w-150">Network RC:</p>
                <p className="bo-consumer-text">
                  {card?.recentValidation?.networkResponseCode || '---'}
                </p>
              </div>
              <div className="d-flex mb-1 align-items-top overflow-hidden">
                <p className="bo-consumer-text bo-min-w-150">Status:</p>
                <div className="d-flex flex-wrap align-items-center gap-1">
                  <Badge
                    className="bo-consumer-inline-badge text-truncate"
                    bg={CARD_STATUS[card.status]?.color}
                  >
                    {CARD_STATUS[card.status]?.text}
                    {(card.status === 'OK' || card.status === 'INVALID') && card.manualStatus
                      ? ' (manual)'
                      : ''}
                  </Badge>
                  {card.status === 'PENDING_APPROVAL' && (
                    <>
                      <Button
                        className="bo-common-inline-button px-1 py-0 text-nowrap"
                        onClick={() =>
                          sendCardRequest({ url: 'approve_pending', data: { cardId: card.cardId } })
                        }
                      >
                        Approve
                      </Button>
                      <Button
                        className="bo-common-inline-button px-1 py-0 text-nowrap"
                        onClick={() =>
                          sendCardRequest({ url: 'reject_pending', data: { cardId: card.cardId } })
                        }
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {card.status === 'INVALID' && consumerManualCardValidation && (
                    <Button
                      className="bo-common-inline-button px-1 py-0 text-nowrap"
                      onClick={() =>
                        sendCardRequest({ url: 'approve_invalid', data: { cardId: card.cardId } })
                      }
                    >
                      Approve
                    </Button>
                  )}
                  <Button
                    className="bo-common-inline-button px-1 py-0 text-nowrap"
                    onClick={() =>
                      sendCardRequest({
                        url: card.status === 'BLOCKED_BY_BRIDGEOVER' ? 'unblock' : 'block',
                        data: { cardId: card.cardId },
                      })
                    }
                  >
                    {card.status === 'BLOCKED_BY_BRIDGEOVER' ? 'Unblock' : 'Block'}
                  </Button>
                </div>
              </div>
              <hr className="my-2" />
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-consumer-text bo-min-w-150">Bank Account:</p>
                {getBankAccountValue(card)}
              </div>
            </div>
            <div className="mt-2 d-flex align-items-center gap-2">
              <div className="d-flex bo-w-150">
                {!card.isAdhoc && (
                  <Button
                    className="bo-common-inline-button"
                    onClick={() =>
                      sendCardRequest({
                        url: 'set_main',
                        data: { consumerId, cardId: card.cardId },
                      })
                    }
                    disabled={card.isMain}
                  >
                    Set as main card
                  </Button>
                )}
              </div>
              <div className="d-flex flex-grow-1 justify-content-between gap-2">
                <Button
                  className="bo-common-inline-button text-nowrap"
                  onClick={() =>
                    setShowLog(prev =>
                      prev.includes(card.cardId)
                        ? prev.filter(x => x !== card.cardId)
                        : [...prev, card.cardId],
                    )
                  }
                >
                  {showLog.includes(card.cardId) ? 'Hide Log' : 'Show Log'}
                </Button>
                <Button
                  className="bo-common-inline-button text-nowrap"
                  onClick={() =>
                    setMoreInfo(prev =>
                      prev.includes(card.cardId)
                        ? prev.filter(x => x !== card.cardId)
                        : [...prev, card.cardId],
                    )
                  }
                >
                  {moreInfo.includes(card.cardId) ? 'Hide Info' : 'More Info'}
                </Button>
                <Button
                  className="bo-common-inline-button"
                  onClick={() =>
                    sendCardRequest({
                      url: 'remove',
                      data: { consumerId, cardId: card.cardId },
                      askForConfirmation: true,
                    })
                  }
                >
                  Delete
                </Button>
              </div>
            </div>
            {showLog.includes(card.cardId) && (
              <div>
                <hr className="my-2" />
                {card.log
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                  .map((x, idx) => (
                    <div
                      key={x.id}
                      className={`d-flex align-items-center ${idx % 2 !== 0 ? 'bg-light' : ''}`}
                    >
                      <div className="bo-min-w-150">
                        <p className="bo-consumer-text">
                          {moment(x.timestamp).format('YYYY-MM-DD')}
                        </p>
                        <p className="bo-consumer-text">{moment(x.timestamp).format('HH:mm:ss')}</p>
                      </div>
                      <p className="bo-consumer-text">{x.details}</p>
                    </div>
                  ))}
              </div>
            )}
            {moreInfo.includes(card.cardId) && (
              <div>
                <hr className="my-2" />
                <div className="d-flex align-items-center">
                  <p className="bo-consumer-text bo-min-w-150">Regulated</p>
                  <p className="bo-consumer-text">{card.regulated ? 'True' : 'False'}</p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-consumer-text bo-min-w-150">AVS ID</p>
                  <p className="bo-consumer-text">{card?.recentValidation?.avsId || '---'}</p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-consumer-text bo-min-w-150">AVS Code</p>
                  <p className="bo-consumer-text">{card?.recentValidation?.avsResponse || '---'}</p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-consumer-text bo-min-w-150">AVS Security Code</p>
                  <p className="bo-consumer-text">
                    {card?.recentValidation?.secutiryCodeResponse || '---'}
                  </p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-consumer-text bo-min-w-150">ANI Match Code</p>
                  <p className="bo-consumer-text">
                    {card?.recentValidation?.aniMatchCode || '---'}
                  </p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-consumer-text bo-min-w-150">ANI FN Match Code</p>
                  <p className="bo-consumer-text">
                    {card?.recentValidation?.aniFullNameMatchCode || '---'}
                  </p>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

export default ConsumerTabaPayCardsSection;
